import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Factory, RefreshCw, Plus, FileText, Edit3, Trash2 } from 'lucide-react';

const API = window.location.origin;

export const GilbertsTab = ({ token, onShiftsChange, onInvoiceCreated }) => {
  const [gilbertsShifts, setGilbertsShifts] = useState([]);
  const [gilbertsRates, setGilbertsRates] = useState({ 
    cnc: { label: "CNC", rate: 27.50 }, 
    maintenance: { label: "Maintenance", rate: 20.00 } 
  });
  const [isAddShiftOpen, setIsAddShiftOpen] = useState(false);
  const [isEditShiftOpen, setIsEditShiftOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [newShift, setNewShift] = useState({
    date: new Date().toISOString().split('T')[0],
    sandy_hours: 10,
    sandy_role: 'cnc',
    mark_hours: 10,
    mark_role: 'maintenance',
    notes: ''
  });
  
  // Date range filter
  const [dateFilter, setDateFilter] = useState('all'); // all, this_week, this_month, last_month
  const [selectedShifts, setSelectedShifts] = useState([]); // For batch selection

  // eslint-disable-next-line react-hooks/exhaustive-deps -- authHeaders is stable via useMemo
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  
  // Filter shifts by date range
  const getFilteredShifts = () => {
    if (dateFilter === 'all') return gilbertsShifts;
    
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    
    return gilbertsShifts.filter(shift => {
      const shiftDate = new Date(shift.date);
      switch (dateFilter) {
        case 'this_week':
          return shiftDate >= startOfWeek;
        case 'this_month':
          return shiftDate >= startOfMonth;
        case 'last_month':
          return shiftDate >= startOfLastMonth && shiftDate <= endOfLastMonth;
        default:
          return true;
      }
    });
  };
  
  // Calculate summary stats
  const getStats = () => {
    const filtered = getFilteredShifts();
    const uninvoiced = filtered.filter(s => !s.invoiced);
    const invoiced = filtered.filter(s => s.invoiced);
    
    const totalHours = filtered.reduce((sum, s) => {
      return sum + (s.entries?.reduce((h, e) => h + (e.hours || 0), 0) || 0);
    }, 0);
    
    const sandyHours = filtered.reduce((sum, s) => {
      return sum + (s.entries?.filter(e => e.admin_name?.toLowerCase().includes('sandy')).reduce((h, e) => h + (e.hours || 0), 0) || 0);
    }, 0);
    
    const markHours = filtered.reduce((sum, s) => {
      return sum + (s.entries?.filter(e => e.admin_name?.toLowerCase().includes('mark')).reduce((h, e) => h + (e.hours || 0), 0) || 0);
    }, 0);
    
    return {
      totalShifts: filtered.length,
      uninvoicedCount: uninvoiced.length,
      invoicedCount: invoiced.length,
      totalHours,
      sandyHours,
      markHours,
      uninvoicedTotal: uninvoiced.reduce((sum, s) => sum + (s.total_amount || 0), 0),
      invoicedTotal: invoiced.reduce((sum, s) => sum + (s.total_amount || 0), 0)
    };
  };
  
  // Toggle shift selection for batch operations
  const toggleShiftSelection = (shiftId) => {
    setSelectedShifts(prev => 
      prev.includes(shiftId) 
        ? prev.filter(id => id !== shiftId)
        : [...prev, shiftId]
    );
  };
  
  // Select all uninvoiced shifts
  const selectAllUninvoiced = () => {
    const uninvoicedIds = getFilteredShifts().filter(s => !s.invoiced).map(s => s.id);
    setSelectedShifts(uninvoicedIds);
  };
  
  // Create batch invoice for selected shifts
  const handleBatchInvoice = async () => {
    if (selectedShifts.length === 0) {
      toast.error("Select at least one shift to invoice");
      return;
    }
    
    try {
      // Send shift IDs directly to the new endpoint
      const response = await axios.post(`${API}/api/admin/gilberts/generate-invoice`, {
        shift_ids: selectedShifts
      }, authHeaders);
      
      toast.success(`Invoice ${response.data.reference} generated - £${response.data.total?.toFixed(2) || 'N/A'}`);
      setSelectedShifts([]);
      fetchGilbertsShifts();
      if (onInvoiceCreated) onInvoiceCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate batch invoice");
    }
  };

  // Fetch Gilberts shifts
  const fetchGilbertsShifts = useCallback(async () => {
    try {
      const [shiftsRes, ratesRes] = await Promise.all([
        axios.get(`${API}/api/admin/gilberts/shifts`, authHeaders),
        axios.get(`${API}/api/admin/gilberts/rates`, authHeaders)
      ]);
      setGilbertsShifts(shiftsRes.data);
      if (ratesRes.data?.rates) setGilbertsRates(ratesRes.data.rates);
      // Notify parent of shifts change for calendar integration
      if (onShiftsChange) onShiftsChange(shiftsRes.data);
    } catch (error) {
      console.error("Error fetching Gilberts data:", error);
    }
  }, [authHeaders, onShiftsChange]);

  useEffect(() => {
    if (token) {
      fetchGilbertsShifts();
    }
  }, [token, fetchGilbertsShifts]);

  // Log Gilberts shift
  const handleLogGilbertsShift = async () => {
    try {
      const entries = [];
      if (newShift.sandy_hours > 0) {
        entries.push({
          admin_id: "sandy",
          admin_name: "Sandy",
          role: newShift.sandy_role,
          hours: parseFloat(newShift.sandy_hours),
          rate: gilbertsRates[newShift.sandy_role]?.rate || 27.50
        });
      }
      if (newShift.mark_hours > 0) {
        entries.push({
          admin_id: "mark",
          admin_name: "Mark",
          role: newShift.mark_role,
          hours: parseFloat(newShift.mark_hours),
          rate: gilbertsRates[newShift.mark_role]?.rate || 20.00
        });
      }
      await axios.post(`${API}/api/admin/gilberts/shifts`, {
        date: newShift.date,
        entries,
        notes: newShift.notes
      }, authHeaders);
      toast.success("Shift logged!");
      setIsAddShiftOpen(false);
      setNewShift({ date: new Date().toISOString().split('T')[0], sandy_hours: 10, sandy_role: 'cnc', mark_hours: 10, mark_role: 'maintenance', notes: '' });
      fetchGilbertsShifts();
    } catch (error) {
      toast.error("Failed to log shift");
    }
  };

  // Open edit shift dialog
  const openEditShift = (shift) => {
    const sandyEntry = shift.entries?.find(e => e.admin_name === 'Sandy') || {};
    const markEntry = shift.entries?.find(e => e.admin_name === 'Mark') || {};
    setEditingShift({
      id: shift.id,
      date: shift.date,
      sandy_hours: sandyEntry.hours || 0,
      sandy_role: sandyEntry.role || 'cnc',
      mark_hours: markEntry.hours || 0,
      mark_role: markEntry.role || 'maintenance',
      notes: shift.notes || ''
    });
    setIsEditShiftOpen(true);
  };

  // Update shift
  const handleUpdateShift = async () => {
    if (!editingShift) return;
    try {
      // Delete old shift and create new one
      await axios.delete(`${API}/api/admin/gilberts/shifts/${editingShift.id}`, authHeaders);
      
      const entries = [];
      if (editingShift.sandy_hours > 0) {
        entries.push({
          admin_id: "sandy",
          admin_name: "Sandy",
          role: editingShift.sandy_role,
          hours: parseFloat(editingShift.sandy_hours),
          rate: gilbertsRates[editingShift.sandy_role]?.rate || 27.50
        });
      }
      if (editingShift.mark_hours > 0) {
        entries.push({
          admin_id: "mark",
          admin_name: "Mark",
          role: editingShift.mark_role,
          hours: parseFloat(editingShift.mark_hours),
          rate: gilbertsRates[editingShift.mark_role]?.rate || 20.00
        });
      }
      
      await axios.post(`${API}/api/admin/gilberts/shifts`, {
        date: editingShift.date,
        entries,
        notes: editingShift.notes
      }, authHeaders);
      
      toast.success("Shift updated!");
      setIsEditShiftOpen(false);
      setEditingShift(null);
      fetchGilbertsShifts();
    } catch (error) {
      toast.error("Failed to update shift");
    }
  };

  // Delete shift
  const handleDeleteShift = async (shiftId) => {
    if (!confirm('Delete this shift?')) return;
    try {
      await axios.delete(`${API}/api/admin/gilberts/shifts/${shiftId}`, authHeaders);
      toast.success("Shift deleted");
      fetchGilbertsShifts();
    } catch (error) {
      toast.error("Failed to delete shift");
    }
  };

  // Generate invoice
  const handleGenerateGilbertsInvoice = async (weekEnding) => {
    try {
      const response = await axios.post(`${API}/api/admin/gilberts/generate-invoice?week_ending=${weekEnding}`, {}, authHeaders);
      toast.success(`Invoice ${response.data.reference} generated - £${response.data.total.toFixed(2)}`);
      fetchGilbertsShifts();
      if (onInvoiceCreated) onInvoiceCreated();
    } catch (error) {
      toast.error("Failed to generate invoice");
    }
  };

  // Calculate total for new shift preview
  const calculateShiftTotal = (shift) => {
    const sandyRate = gilbertsRates[shift.sandy_role]?.rate || 27.50;
    const markRate = gilbertsRates[shift.mark_role]?.rate || 20.00;
    return (shift.sandy_hours * sandyRate) + (shift.mark_hours * markRate);
  };

  return (
    <div className="space-y-6">
      {/* Gilberts Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Factory className="h-5 w-5" />
                JR Gilberts Engineering
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Mon/Tue/Wed • 4-week rotation</p>
            </div>
            <div className="flex gap-2">
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="this_week">This Week</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={fetchGilbertsShifts}>
                <RefreshCw className="h-4 w-4 mr-1" />Refresh
              </Button>
              <Button size="sm" onClick={() => setIsAddShiftOpen(true)} data-testid="log-shift-btn">
                <Plus className="h-4 w-4 mr-1" />Log Shift
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            const stats = getStats();
            return (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600">Sandy Hours</p>
                  <p className="text-2xl font-bold text-blue-800">{stats.sandyHours}h</p>
                  <p className="text-xs text-blue-600">@ £{gilbertsRates.cnc?.rate || 27.50}/hr</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600">Mark Hours</p>
                  <p className="text-2xl font-bold text-green-800">{stats.markHours}h</p>
                  <p className="text-xs text-green-600">@ £{gilbertsRates.maintenance?.rate || 20.00}/hr</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-purple-600">Total Hours</p>
                  <p className="text-2xl font-bold text-purple-800">{stats.totalHours}h</p>
                  <p className="text-xs text-purple-600">{stats.totalShifts} shifts</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-sm text-amber-600">Uninvoiced</p>
                  <p className="text-2xl font-bold text-amber-800">£{stats.uninvoicedTotal.toFixed(2)}</p>
                  <p className="text-xs text-amber-600">{stats.uninvoicedCount} shifts</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <p className="text-sm text-emerald-600">Invoiced</p>
                  <p className="text-2xl font-bold text-emerald-800">£{stats.invoicedTotal.toFixed(2)}</p>
                  <p className="text-xs text-emerald-600">{stats.invoicedCount} shifts</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600">Period Total</p>
                  <p className="text-2xl font-bold text-slate-800">£{(stats.uninvoicedTotal + stats.invoicedTotal).toFixed(2)}</p>
                  <p className="text-xs text-slate-600">{dateFilter === 'all' ? 'All time' : dateFilter.replace('_', ' ')}</p>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Recent Shifts */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading">
              Shifts
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({getFilteredShifts().length} {dateFilter === 'all' ? 'total' : `in ${dateFilter.replace('_', ' ')}`})
              </span>
            </CardTitle>
            <div className="flex gap-2">
              {selectedShifts.length > 0 && (
                <Button 
                  size="sm"
                  variant="default"
                  onClick={handleBatchInvoice}
                  data-testid="batch-invoice-btn"
                >
                  <FileText className="h-4 w-4 mr-1" />Invoice {selectedShifts.length} Selected
                </Button>
              )}
              {getFilteredShifts().filter(s => !s.invoiced).length > 0 && (
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={selectAllUninvoiced}
                  data-testid="select-all-btn"
                >
                  Select All Uninvoiced
                </Button>
              )}
              {gilbertsShifts.filter(s => !s.invoiced).length > 0 && selectedShifts.length === 0 && (
                <Button 
                  size="sm" 
                  onClick={() => {
                    const lastShift = gilbertsShifts.filter(s => !s.invoiced).sort((a, b) => b.date.localeCompare(a.date))[0];
                    if (lastShift) handleGenerateGilbertsInvoice(lastShift.date);
                  }}
                  data-testid="generate-invoice-btn"
                >
                  <FileText className="h-4 w-4 mr-1" />Generate Invoice
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {getFilteredShifts().length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No shifts for this period. {dateFilter !== 'all' && 'Try selecting a different date range.'}</p>
          ) : (
            <div className="space-y-3">
              {getFilteredShifts().slice(0, 20).map(shift => (
                <div 
                  key={shift.id} 
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    shift.invoiced 
                      ? 'bg-gray-50' 
                      : selectedShifts.includes(shift.id) 
                        ? 'bg-blue-50 border-blue-300' 
                        : 'bg-white hover:bg-gray-50'
                  }`} 
                  onClick={() => !shift.invoiced && toggleShiftSelection(shift.id)}
                  data-testid={`shift-${shift.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {!shift.invoiced && (
                        <input 
                          type="checkbox" 
                          checked={selectedShifts.includes(shift.id)}
                          onChange={() => toggleShiftSelection(shift.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      )}
                      <div>
                        <p className="font-medium">{new Date(shift.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        <div className="flex gap-4 mt-2">
                          {shift.entries?.map((entry, i) => (
                            <div key={i} className="text-sm">
                              <span className="font-medium">{entry.admin_name}</span>
                              <span className="text-muted-foreground"> • {entry.hours}hrs {entry.role === 'cnc' ? 'CNC' : 'Maintenance'}</span>
                              <span className="text-muted-foreground"> • £{entry.total?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        {shift.notes && <p className="text-xs text-muted-foreground mt-1 italic">{shift.notes}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-lg font-bold">£{shift.total_amount?.toFixed(2)}</p>
                        {shift.invoiced ? (
                          <Badge variant="secondary">Invoiced</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditShift(shift); }} title="Edit Shift" data-testid={`edit-shift-${shift.id}`}>
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteShift(shift.id); }} title="Delete Shift" className="text-red-500 hover:text-red-700" data-testid={`delete-shift-${shift.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Shift Dialog */}
      <Dialog open={isAddShiftOpen} onOpenChange={setIsAddShiftOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Gilberts Shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input type="date" value={newShift.date} onChange={(e) => setNewShift({...newShift, date: e.target.value})} data-testid="shift-date-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sandy - Hours</Label>
                <Input type="number" value={newShift.sandy_hours} onChange={(e) => setNewShift({...newShift, sandy_hours: parseFloat(e.target.value) || 0})} data-testid="sandy-hours-input" />
              </div>
              <div className="space-y-2">
                <Label>Sandy - Role</Label>
                <Select value={newShift.sandy_role} onValueChange={(v) => setNewShift({...newShift, sandy_role: v})}>
                  <SelectTrigger data-testid="sandy-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cnc">CNC (£{gilbertsRates.cnc?.rate || 27.50}/hr)</SelectItem>
                    <SelectItem value="maintenance">Maintenance (£{gilbertsRates.maintenance?.rate || 20.00}/hr)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mark - Hours</Label>
                <Input type="number" value={newShift.mark_hours} onChange={(e) => setNewShift({...newShift, mark_hours: parseFloat(e.target.value) || 0})} data-testid="mark-hours-input" />
              </div>
              <div className="space-y-2">
                <Label>Mark - Role</Label>
                <Select value={newShift.mark_role} onValueChange={(v) => setNewShift({...newShift, mark_role: v})}>
                  <SelectTrigger data-testid="mark-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cnc">CNC (£{gilbertsRates.cnc?.rate || 27.50}/hr)</SelectItem>
                    <SelectItem value="maintenance">Maintenance (£{gilbertsRates.maintenance?.rate || 20.00}/hr)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input value={newShift.notes} onChange={(e) => setNewShift({...newShift, notes: e.target.value})} placeholder="Any notes for this shift" data-testid="shift-notes-input" />
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Estimated Total</p>
              <p className="text-2xl font-bold">£{calculateShiftTotal(newShift).toFixed(2)}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddShiftOpen(false)}>Cancel</Button>
            <Button onClick={handleLogGilbertsShift} data-testid="submit-shift-btn">Log Shift</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Shift Dialog */}
      <Dialog open={isEditShiftOpen} onOpenChange={setIsEditShiftOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Shift</DialogTitle>
          </DialogHeader>
          {editingShift && (
            <div className="space-y-4">
              <div>
                <Label>Date</Label>
                <Input type="date" value={editingShift.date} onChange={(e) => setEditingShift({...editingShift, date: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sandy - Hours</Label>
                  <Input type="number" value={editingShift.sandy_hours} onChange={(e) => setEditingShift({...editingShift, sandy_hours: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="space-y-2">
                  <Label>Sandy - Role</Label>
                  <Select value={editingShift.sandy_role} onValueChange={(v) => setEditingShift({...editingShift, sandy_role: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cnc">CNC (£{gilbertsRates.cnc?.rate || 27.50}/hr)</SelectItem>
                      <SelectItem value="maintenance">Maintenance (£{gilbertsRates.maintenance?.rate || 20.00}/hr)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mark - Hours</Label>
                  <Input type="number" value={editingShift.mark_hours} onChange={(e) => setEditingShift({...editingShift, mark_hours: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="space-y-2">
                  <Label>Mark - Role</Label>
                  <Select value={editingShift.mark_role} onValueChange={(v) => setEditingShift({...editingShift, mark_role: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cnc">CNC (£{gilbertsRates.cnc?.rate || 27.50}/hr)</SelectItem>
                      <SelectItem value="maintenance">Maintenance (£{gilbertsRates.maintenance?.rate || 20.00}/hr)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={editingShift.notes} onChange={(e) => setEditingShift({...editingShift, notes: e.target.value})} />
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Estimated Total</p>
                <p className="text-2xl font-bold">£{calculateShiftTotal(editingShift).toFixed(2)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditShiftOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateShift} data-testid="update-shift-btn">Update Shift</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GilbertsTab;
